import { useState, useCallback, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isWithinInterval,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/components/ui/utils';
import { CertificationHealthBadge } from '@/app/pages/crew/certification-health';
import type { AvailabilityEntry, AvailabilityStatus, TechProfileListItem } from '@/lib/types';

// ─── Status Colors ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<AvailabilityStatus, { bg: string; text: string; label: string }> = {
  available: { bg: 'bg-green-200', text: 'text-green-800', label: 'Available' },
  unavailable: { bg: 'bg-red-200', text: 'text-red-800', label: 'Unavailable' },
  tentative: { bg: 'bg-yellow-200', text: 'text-yellow-800', label: 'Tentative' },
  on_project: { bg: 'bg-blue-200', text: 'text-blue-800', label: 'On Project' },
  unknown: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Unknown' },
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface AvailabilityCalendarProps {
  entries: AvailabilityEntry[];
  month?: Date;
  onDateClick?: (date: string, currentStatus: AvailabilityStatus | null) => void;
  onDateRangeSelect?: (startDate: string, endDate: string) => void;
  onMonthChange?: (month: Date) => void;
  isEditable?: boolean;
  className?: string;
}

interface TeamViewProps {
  techs: TechProfileListItem[];
  entries: AvailabilityEntry[];
  month: Date;
  onTechClick?: (techId: number) => void;
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function StatusLegend() {
  return (
    <div className="flex flex-wrap gap-3 text-xs">
      {Object.entries(STATUS_COLORS).map(([status, colors]) => (
        <div key={status} className="flex items-center gap-1.5">
          <div className={cn('w-3 h-3 rounded-sm', colors.bg)} />
          <span className="text-gray-600">{colors.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Single Tech Calendar ────────────────────────────────────────────────────

export function AvailabilityCalendar({
  entries,
  month,
  onDateClick,
  onDateRangeSelect,
  onMonthChange,
  isEditable = false,
  className,
}: AvailabilityCalendarProps) {
  const [internalMonth, setInternalMonth] = useState(() => month ?? new Date());
  const currentMonth = month ?? internalMonth;
  const [dragStart, setDragStart] = useState<Date | null>(null);
  const [dragEnd, setDragEnd] = useState<Date | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const entryMap = useMemo(() => {
    const map = new Map<string, AvailabilityEntry>();
    entries.forEach((e) => map.set(e.date, e));
    return map;
  }, [entries]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);

  const weeks: Date[][] = [];
  let day = calStart;
  while (day <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  const isInDragRange = useCallback(
    (date: Date) => {
      if (!dragStart || !dragEnd) return false;
      const start = dragStart < dragEnd ? dragStart : dragEnd;
      const end = dragStart < dragEnd ? dragEnd : dragStart;
      return isWithinInterval(date, { start, end });
    },
    [dragStart, dragEnd]
  );

  const handleMouseDown = (date: Date) => {
    if (!isEditable) return;
    setDragStart(date);
    setDragEnd(date);
    setIsDragging(true);
  };

  const handleMouseEnter = (date: Date) => {
    if (isDragging) {
      setDragEnd(date);
    }
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd) {
      setIsDragging(false);
      return;
    }

    const start = dragStart < dragEnd ? dragStart : dragEnd;
    const end = dragStart < dragEnd ? dragEnd : dragStart;

    if (isSameDay(start, end)) {
      const dateStr = format(start, 'yyyy-MM-dd');
      const entry = entryMap.get(dateStr);
      const status = entry?.status ?? 'unknown';
      onDateClick?.(dateStr, status);
    } else {
      onDateRangeSelect?.(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
    }

    setDragStart(null);
    setDragEnd(null);
    setIsDragging(false);
  };

  return (
    <div className={cn('select-none', className)} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button aria-label="Previous month" variant="outline" size="sm" onClick={() => { const m = subMonths(currentMonth, 1); setInternalMonth(m); onMonthChange?.(m); }}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold">{format(currentMonth, 'MMMM yyyy')}</h3>
        <Button aria-label="Next month" variant="outline" size="sm" onClick={() => { const m = addMonths(currentMonth, 1); setInternalMonth(m); onMonthChange?.(m); }}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-center text-xs font-medium text-gray-500 py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((date, idx) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const entry = entryMap.get(dateStr);
          const inMonth = isSameMonth(date, currentMonth);
          const isToday = isSameDay(date, new Date());
          const inDrag = isInDragRange(date);
          const status = entry?.status ?? 'unknown';
          const statusColor = STATUS_COLORS[status];

          return (
            <div
              key={idx}
              aria-label={`${dateStr}: ${statusColor.label}`}
              title={`${dateStr}: ${statusColor.label}`}
              className={cn(
                'relative aspect-square flex flex-col items-center justify-center rounded-md text-sm transition-colors',
                isEditable && 'cursor-pointer hover:ring-2 hover:ring-blue-300',
                !inMonth && 'opacity-30',
                isToday && 'ring-2 ring-blue-500',
                inDrag && 'ring-2 ring-blue-400 bg-blue-50',
                statusColor.bg,
                statusColor.text
              )}
              onMouseDown={() => handleMouseDown(date)}
              onMouseEnter={() => handleMouseEnter(date)}
            >
              <span className={cn('text-xs sm:text-sm font-medium', isToday && 'font-bold')}>
                {format(date, 'd')}
              </span>
              {status !== 'unknown' && (
                <span className="hidden sm:block text-[10px] leading-tight mt-0.5">
                  {statusColor.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4">
        <StatusLegend />
      </div>
    </div>
  );
}

// ─── Team View (Gantt-style) ─────────────────────────────────────────────────

export function TeamAvailabilityView({ techs, entries, month, onTechClick }: TeamViewProps) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const daysInMonth: Date[] = [];
  let d = monthStart;
  while (d <= monthEnd) {
    daysInMonth.push(d);
    d = addDays(d, 1);
  }

  const entryMap = useMemo(() => {
    const map = new Map<string, AvailabilityEntry>();
    entries.forEach((e) => map.set(`${e.tech}-${e.date}`, e));
    return map;
  }, [entries]);

  return (
    <div>
      {/* Desktop gantt view */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Header row */}
          <div className="flex border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="w-48 flex-shrink-0 p-2 font-medium text-sm text-gray-700 border-r border-gray-200">
              Technician
            </div>
            <div className="flex flex-1">
              {daysInMonth.map((day) => (
                <div
                  key={day.toISOString()}
                  className={cn(
                    'flex-1 min-w-[28px] text-center text-[10px] py-1 border-r border-gray-100',
                    isSameDay(day, new Date()) && 'bg-blue-50 font-bold'
                  )}
                >
                  <div>{format(day, 'd')}</div>
                  <div className="text-gray-400">{format(day, 'EEE').charAt(0)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tech rows */}
          {techs.map((tech) => (
            <div key={tech.id} className="flex border-b border-gray-100 hover:bg-gray-50">
              <div
                className={cn(
                  'w-48 flex-shrink-0 p-2 text-sm border-r border-gray-200 truncate',
                  onTechClick && 'cursor-pointer hover:text-blue-600'
                )}
                onClick={() => onTechClick?.(tech.id)}
              >
                <div className="font-medium">{tech.full_name}</div>
                <div className="text-xs text-gray-500 truncate">{tech.job_title}</div>
                <CertificationHealthBadge
                  certifications={tech.certification_summary}
                  compact
                />
              </div>
              <div className="flex flex-1">
                {daysInMonth.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const entry = entryMap.get(`${tech.id}-${dateStr}`);
                  const colors = entry ? STATUS_COLORS[entry.status] : STATUS_COLORS.unknown;
                  return (
                    <div
                      key={dateStr}
                      className={cn(
                        'flex-1 min-w-[28px] min-h-[32px] border-r border-gray-100',
                        colors.bg
                      )}
                      title={`${tech.full_name}: ${colors.label} (${dateStr})`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile collapsible day list */}
      <div className="lg:hidden space-y-1">
        {daysInMonth.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const isToday = isSameDay(day, new Date());
          const techEntries = techs.map((tech) => ({
            tech,
            entry: entryMap.get(`${tech.id}-${dateStr}`),
          }));
          return (
            <details
              key={dateStr}
              className={cn(
                'border rounded-lg',
                isToday && 'ring-2 ring-blue-500'
              )}
              open={isToday}
            >
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer min-h-[44px] select-none">
                <span className={cn('text-sm font-medium', isToday && 'text-blue-700')}>
                  {format(day, 'EEE, MMM d')}
                </span>
                <span className="text-xs text-gray-500">
                  {techEntries.filter((te) => te.entry).length}/{techs.length} logged
                </span>
              </summary>
              <div className="px-4 pb-3 flex flex-wrap gap-2">
                {techEntries.length === 0 ? (
                  <p className="text-sm text-gray-400">No technicians</p>
                ) : (
                  techEntries.map(({ tech, entry }) => {
                    const colors = entry ? STATUS_COLORS[entry.status] : STATUS_COLORS.unknown;
                    return (
                      <div key={tech.id} className="flex flex-wrap items-center gap-1">
                        <Badge
                          className={cn(
                            'text-xs py-1 px-2 min-h-[28px]',
                            `${colors.bg} ${colors.text}`,
                            onTechClick && 'cursor-pointer'
                          )}
                          onClick={() => onTechClick?.(tech.id)}
                          onKeyDown={onTechClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTechClick(tech.id); } } : undefined}
                          role={onTechClick ? 'button' : undefined}
                          tabIndex={onTechClick ? 0 : undefined}
                        >
                          {tech.full_name}
                          <span className="ml-1 opacity-75">· {colors.label}</span>
                        </Badge>
                        <CertificationHealthBadge
                          certifications={tech.certification_summary}
                          compact
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </details>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3">
        <StatusLegend />
      </div>
    </div>
  );
}
